/**
 * Technische Anlagen (multi) + Auftrag (single) comboboxes on the Impower document editor.
 * Options: domain-wide via GET /api/technical-assets/tree and
 * GET /api/work-orders/by-technical-asset/:id and
 * GET /api/work-orders/without-technical-asset. Persistence: PUT /api/document/impower-links
 */

const LUX_DOC_SUBJECT_LINKS_SPIN_STYLE_ID =
  'lux-document-editor-combobox-spin-keyframes'

function ensureLuxDocSubjectLinksSpinKeyframes() {
  if (document.getElementById(LUX_DOC_SUBJECT_LINKS_SPIN_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = LUX_DOC_SUBJECT_LINKS_SPIN_STYLE_ID
  style.textContent = `
    @keyframes luxDocumentEditorSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `
  document.head.appendChild(style)
}

const documentEditorSubjectLinksComboBox = () => {
  const generalBlock = document.querySelector('#GeneralDocumentData')
  if (!generalBlock) return

  let section = document.querySelector(
    '#GeneralDocumentData .section-content > .ant-row'
  )
  if (!section) return
  if (section.querySelector('#luxDocumentTechnicalAssetsComboBox')) return

  ensureLuxDocSubjectLinksSpinKeyframes()
  if (typeof ensureDocumentEditorComboSpinKeyframes === 'function') {
    ensureDocumentEditorComboSpinKeyframes()
  }

  const currentDocumentId =
    typeof parseDocumentHrIdFromLocation === 'function'
      ? parseDocumentHrIdFromLocation()
      : window.location.pathname.split('/').filter(Boolean).pop()

  let dataLoaded = false
  let editMode = false

  let taTree = null
  /** @type {Map<number, { id: number, name: string, typ: string, buildingName: string, propertyName: string }>} */
  let taLabelsById = new Map()
  let woTaTree = null
  /** @type {Array<{ id: number, type: string, occurredAtDate: string | null, label: string }> | null} */
  let workOrdersWithoutTa = null
  let woUnlinkedSectionOpen = false
  let woUnlinkedLoadPromise = null
  /** @type {Map<number, { id: number, type: string, occurredAtDate: string | null, label: string }[]>} */
  const workOrdersByTaId = new Map()
  const woLoadingTaIds = new Set()

  const taExpandedProperties = new Set()
  const taExpandedBuildings = new Set()
  const woExpandedProperties = new Set()
  const woExpandedBuildings = new Set()
  const woExpandedTaIds = new Set()

  let selectedAssetIds = []
  let committedAssetIds = []

  let selectedWorkOrderId = null
  let committedWorkOrderId = null
  let workOrderDisplayLabel = ''

  const removingAssetIds = new Set()
  let taTreeLoadPromise = null
  let woTaTreeLoadPromise = null
  let persistChain = Promise.resolve()
  let persistInFlight = 0
  let lastSyncedEditMode = null

  function createSpinnerEl(sizePx) {
    const el = document.createElement('div')
    el.setAttribute('aria-hidden', 'true')
    el.style.cssText = `border:2px solid #f3f3f3;border-top:2px solid #1890ff;border-radius:50%;width:${sizePx}px;height:${sizePx}px;animation:luxDocumentEditorSpin 0.8s linear infinite;flex-shrink:0;box-sizing:border-box;`
    return el
  }

  const authHeader = () => {
    try {
      return JSON.parse(localStorage.auth).token
    } catch (_) {
      return null
    }
  }

  function workOrderTypeLabel(type) {
    if (type === 'MAINTENANCE') return 'Wartung'
    if (type === 'REPAIR') return 'Reparatur'
    return type || ''
  }

  function formatIsoDateDe(v) {
    if (!v) return ''
    const iso = String(v).slice(0, 10)
    const parts = iso.split('-')
    if (parts.length !== 3) return iso
    return `${parts[2]}.${parts[1]}.${parts[0]}`
  }

  function workOrderLabel(wo) {
    const date = formatIsoDateDe(wo.occurredAtDate)
    return `#${wo.id} ${workOrderTypeLabel(wo.type)}${date ? ` · ${date}` : ''}`
  }

  function flattenTaTreeLabels(tree) {
    const m = new Map()
    if (!tree?.properties) return m
    for (const p of tree.properties) {
      for (const b of p.buildings) {
        for (const a of b.assets) {
          m.set(normalizeAssetId(a.id), {
            id: a.id,
            name: a.name,
            typ: a.typ,
            buildingName: b.name,
            propertyName: p.name,
          })
        }
      }
    }
    return m
  }

  function filterTaTree(tree, query) {
    const q = (query || '').trim().toLowerCase()
    if (!tree?.properties) return []
    if (!q) return tree.properties

    const out = []
    for (const prop of tree.properties) {
      if (prop.name.trim().toLowerCase().includes(q)) {
        out.push(prop)
        continue
      }
      const buildings = []
      for (const b of prop.buildings) {
        if (b.name.trim().toLowerCase().includes(q)) {
          buildings.push(b)
          continue
        }
        const assets = b.assets.filter((a) =>
          a.name.trim().toLowerCase().includes(q)
        )
        if (assets.length > 0) {
          buildings.push({ ...b, assets, assetCount: assets.length })
        }
      }
      if (buildings.length > 0) {
        const assetCount = buildings.reduce(
          (sum, b) => sum + b.assets.length,
          0
        )
        out.push({ ...prop, buildings, assetCount })
      }
    }
    return out
  }

  function expandAllTaKeys(properties) {
    const propertiesSet = new Set()
    const buildingsSet = new Set()
    for (const p of properties) {
      propertiesSet.add(p.id)
      for (const b of p.buildings) {
        buildingsSet.add(`${p.id}:${b.id}`)
      }
    }
    return { properties: propertiesSet, buildings: buildingsSet }
  }

  function normalizeAssetId(id) {
    const n = Number(id)
    return Number.isFinite(n) ? n : id
  }

  function assetDisplayLabel(id) {
    const meta = taLabelsById.get(normalizeAssetId(id))
    if (!meta) return `Anlage #${id}`
    const typ = meta.typ ? ` (${meta.typ})` : ''
    return `${meta.name}${typ}`
  }

  function setsEqual(a, b) {
    const sa = a.map(normalizeAssetId).sort((x, y) => x - y)
    const sb = b.map(normalizeAssetId).sort((x, y) => x - y)
    if (sa.length !== sb.length) return false
    return sa.every((v, i) => v === sb[i])
  }

  function isDirty() {
    return (
      !setsEqual(selectedAssetIds, committedAssetIds) ||
      selectedWorkOrderId !== committedWorkOrderId
    )
  }

  function createComboShell(id, labelText) {
    const wrapper = document.createElement('div')
    wrapper.id = id
    wrapper.className = 'ant-row ant-form-item'
    wrapper.style.marginBottom = '0'
    wrapper.style.padding = '0 16px'

    const label = document.createElement('div')
    label.className = 'ant-col ant-form-item-label'
    label.textContent = labelText

    const content = document.createElement('div')
    content.className = 'ant-col ant-col-24 ant-form-item-control'
    content.style.width = '100%'
    content.style.display = 'block'

    wrapper.appendChild(label)
    wrapper.appendChild(content)
    return { wrapper, content }
  }

  function createDropdownCombo(content, placeholder) {
    const root = document.createElement('div')
    root.style.position = 'relative'
    root.style.width = '100%'

    const display = document.createElement('input')
    display.type = 'text'
    display.className = 'ant-input'
    display.placeholder = placeholder
    display.style.width = '100%'
    display.style.borderRadius = '4px'
    display.style.padding = '6px 40px 6px 16px'
    display.style.fontSize = '1.6rem'
    display.style.cursor = 'pointer'
    display.style.boxShadow = 'none'

    const deleteButton = document.createElement('button')
    deleteButton.innerHTML = '×'
    deleteButton.type = 'button'
    deleteButton.style.cssText =
      'position:absolute;right:8px;top:50%;transform:translateY(-50%);background:transparent;border:none;cursor:pointer;font-size:24px;color:#999;width:24px;height:24px;display:none;line-height:1;padding:0;'

    const drop = document.createElement('div')
    drop.style.cssText =
      'position:absolute;left:0;width:100%;max-height:360px;overflow-y:auto;background:#fff;border:1px solid #d9d9d9;border-radius:4px;box-shadow:0 4px 12px rgba(0,0,0,0.1);display:none;z-index:9999;margin-top:4px;'

    const spinner = document.createElement('div')
    spinner.style.cssText = 'display:none;padding:12px;text-align:center;'
    spinner.innerHTML =
      '<div style="border:2px solid #f3f3f3;border-top:2px solid #1890ff;border-radius:50%;width:20px;height:20px;animation:luxDocumentEditorSpin 1s linear infinite;margin:0 auto;"></div>'

    const opts = document.createElement('div')
    drop.appendChild(spinner)
    drop.appendChild(opts)

    root.appendChild(display)
    root.appendChild(deleteButton)
    root.appendChild(drop)

    content.appendChild(root)

    return { root, display, deleteButton, drop, spinner, opts }
  }

  const assetsShell = createComboShell(
    'luxDocumentTechnicalAssetsComboBox',
    'Technische Anlagen'
  )
  const chipsRow = document.createElement('div')
  chipsRow.style.cssText =
    'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:10px;min-height:0;'
  const assetsCombo = createDropdownCombo(
    assetsShell.content,
    'Liegenschaft, Gebäude oder Anlage…'
  )
  assetsShell.content.insertBefore(chipsRow, assetsCombo.root)
  const woShell = createComboShell('luxDocumentWorkOrderComboBox', 'Auftrag')
  const woCombo = createDropdownCombo(
    woShell.content,
    'Anlage aufklappen, Auftrag wählen…'
  )

  section.appendChild(assetsShell.wrapper)
  section.appendChild(woShell.wrapper)

  async function fetchTechnicalAssetTree(linkScope) {
    const params = new URLSearchParams()
    if (linkScope === 'all') params.set('linkScope', 'all')
    const qs = params.toString() ? `?${params.toString()}` : ''
    // withAuth route: Lux session cookie, no Impower Bearer (see .cursor/rules/with-auth.mdc).
    const response = await fetch(
      `${baseApiUrl}/api/technical-assets/tree${qs}`,
      {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }
    )
    if (!response.ok) return null
    return response.json()
  }

  async function ensureTaTree() {
    if (taTree) return taTree
    if (!taTreeLoadPromise) {
      taTreeLoadPromise = fetchTechnicalAssetTree('all')
        .then((tree) => {
          if (tree) {
            taTree = tree
            taLabelsById = flattenTaTreeLabels(tree)
          }
          return taTree
        })
        .finally(() => {
          taTreeLoadPromise = null
        })
    }
    return taTreeLoadPromise
  }

  async function ensureWoTaTree() {
    if (woTaTree) return woTaTree
    if (!woTaTreeLoadPromise) {
      woTaTreeLoadPromise = fetchTechnicalAssetTree('workOrders')
        .then((tree) => {
          if (tree) woTaTree = tree
          return woTaTree
        })
        .finally(() => {
          woTaTreeLoadPromise = null
        })
    }
    return woTaTreeLoadPromise
  }

  async function ensureWorkOrdersWithoutTa() {
    if (workOrdersWithoutTa) return workOrdersWithoutTa
    if (!woUnlinkedLoadPromise) {
      const token = authHeader()
      if (!token) {
        workOrdersWithoutTa = []
        return workOrdersWithoutTa
      }
      woUnlinkedLoadPromise = fetch(
        `${baseApiUrl}/api/work-orders/without-technical-asset`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      )
        .then(async (response) => {
          if (!response.ok) {
            workOrdersWithoutTa = []
            return workOrdersWithoutTa
          }
          const rows = await response.json()
          workOrdersWithoutTa = (Array.isArray(rows) ? rows : []).map((wo) => ({
            id: wo.id,
            type: wo.type,
            occurredAtDate: wo.occurredAtDate,
            label: wo.label || workOrderLabel(wo),
          }))
          return workOrdersWithoutTa
        })
        .catch((e) => {
          console.error(
            '[documentEditorSubjectLinksComboBox] work orders without TA:',
            e
          )
          workOrdersWithoutTa = []
          return workOrdersWithoutTa
        })
        .finally(() => {
          woUnlinkedLoadPromise = null
        })
    }
    return woUnlinkedLoadPromise
  }

  function appendLoadingRow(container, depth, text) {
    const row = document.createElement('div')
    row.style.cssText = `padding:8px 8px 8px ${8 + depth * 12}px;font-size:1.3rem;color:#666;display:flex;align-items:center;gap:10px;`
    row.appendChild(createSpinnerEl(16))
    const label = document.createElement('span')
    label.textContent = text
    row.appendChild(label)
    container.appendChild(row)
    return row
  }

  async function loadWorkOrdersForTa(taId) {
    const key = normalizeAssetId(taId)
    if (workOrdersByTaId.has(key)) return workOrdersByTaId.get(key)
    const token = authHeader()
    if (!token) return []
    woLoadingTaIds.add(key)
    if (woCombo.drop.style.display !== 'none') {
      renderWorkOrderTree(woCombo.display.value)
    }
    try {
      const response = await fetch(
        `${baseApiUrl}/api/work-orders/by-technical-asset/${encodeURIComponent(String(key))}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      )
      if (!response.ok) {
        workOrdersByTaId.set(key, [])
        return []
      }
      const rows = await response.json()
      const list = (Array.isArray(rows) ? rows : []).map((wo) => ({
        id: wo.id,
        type: wo.type,
        occurredAtDate: wo.occurredAtDate,
        label: workOrderLabel(wo),
      }))
      workOrdersByTaId.set(key, list)
      return list
    } catch (e) {
      console.error('[documentEditorSubjectLinksComboBox] work orders:', e)
      workOrdersByTaId.set(key, [])
      return []
    } finally {
      woLoadingTaIds.delete(key)
      if (woCombo.drop.style.display !== 'none') {
        renderWorkOrderTree(woCombo.display.value)
      }
    }
  }

  async function removeSelectedAsset(assetId) {
    const id = normalizeAssetId(assetId)
    if (removingAssetIds.has(id)) return
    const prev = selectedAssetIds.map(normalizeAssetId)
    if (!prev.includes(id)) return

    removingAssetIds.add(id)
    selectedAssetIds = prev.filter((x) => x !== id)
    renderAssetChips()
    renderAssetTree(assetsCombo.display.value)

    try {
      const ok = await persistSubjectLinksFromCombo({ force: true })
      if (!ok) {
        selectedAssetIds = prev
      }
    } finally {
      removingAssetIds.delete(id)
      renderAssetChips()
      renderAssetTree(assetsCombo.display.value)
    }
  }

  function setReadOnly(readOnly) {
    editMode = !readOnly
    chipsRow.style.pointerEvents = editMode ? 'auto' : 'none'
    for (const combo of [assetsCombo, woCombo]) {
      combo.root.style.pointerEvents = readOnly ? 'none' : 'auto'
      combo.display.disabled = readOnly
      combo.display.style.cursor = readOnly ? 'not-allowed' : 'pointer'
      combo.display.style.opacity = readOnly ? '0.72' : '1'
      combo.deleteButton.style.pointerEvents = readOnly ? 'none' : 'auto'
      if (readOnly) {
        combo.drop.style.display = 'none'
        combo.deleteButton.style.display = 'none'
      }
    }
    woShell.wrapper.style.opacity = readOnly ? '0.72' : '1'
    assetsShell.wrapper.style.opacity = readOnly ? '0.72' : '1'
    if (!readOnly && selectedWorkOrderId != null) {
      woCombo.deleteButton.style.display = 'block'
    }
  }

  function chipDisplayIds() {
    const selected = selectedAssetIds.map(normalizeAssetId)
    const ghost = [...removingAssetIds].filter((id) => !selected.includes(id))
    return [...selected, ...ghost]
  }

  function renderAssetChips() {
    chipsRow.innerHTML = ''
    const ids = chipDisplayIds()
    if (!editMode && ids.length === 0) {
      const empty = document.createElement('span')
      empty.style.cssText = 'font-size:1.4rem;color:#999;'
      empty.textContent = '—'
      chipsRow.appendChild(empty)
      return
    }
    if (ids.length === 0) {
      chipsRow.style.display = 'none'
      return
    }
    chipsRow.style.display = 'flex'
    ids.forEach((id) => {
      const pending = removingAssetIds.has(id)
      const chip = document.createElement('span')
      chip.style.cssText =
        'display:inline-flex;align-items:center;gap:6px;padding:4px 10px;background:#f5f5f5;border:1px solid #d9d9d9;border-radius:4px;font-size:1.3rem;max-width:100%;'
      if (pending) {
        chip.style.opacity = '0.65'
      }
      const labelSpan = document.createElement('span')
      labelSpan.style.minWidth = '0'
      labelSpan.textContent = assetDisplayLabel(id)
      chip.appendChild(labelSpan)
      if (editMode) {
        const rm = document.createElement('button')
        rm.type = 'button'
        rm.dataset.luxTaRemove = String(id)
        rm.style.cssText =
          'border:none;background:transparent;cursor:pointer;font-size:18px;line-height:1;color:#999;padding:0 2px;min-width:18px;min-height:18px;display:inline-flex;align-items:center;justify-content:center;'
        if (pending) {
          rm.disabled = true
          rm.style.cursor = 'default'
          rm.appendChild(createSpinnerEl(14))
        } else {
          rm.textContent = '×'
        }
        chip.appendChild(rm)
      }
      chipsRow.appendChild(chip)
    })
  }

  chipsRow.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-lux-ta-remove]')
    if (!btn || !editMode || btn.disabled) return
    e.stopPropagation()
    e.preventDefault()
    const id = normalizeAssetId(btn.dataset.luxTaRemove)
    removeSelectedAsset(id)
  })

  function appendTreeRow(container, text, depth, opts = {}) {
    const row = document.createElement('div')
    row.style.cssText = `padding:6px 8px 6px ${8 + depth * 12}px;font-size:1.35rem;cursor:${opts.cursor || 'pointer'};font-weight:${opts.bold ? 'bold' : 'normal'};display:flex;align-items:center;gap:6px;`
    if (opts.muted) row.style.color = '#999'
    row.textContent = text
    if (opts.onClick) {
      row.addEventListener('click', (e) => {
        e.stopPropagation()
        opts.onClick()
      })
      row.addEventListener('mouseover', () => {
        row.style.background = '#f5f5f5'
      })
      row.addEventListener('mouseout', () => {
        row.style.background = ''
      })
    }
    container.appendChild(row)
    return row
  }

  function renderAssetTree(filter) {
    assetsCombo.opts.innerHTML = ''
    if (!taTree) {
      return
    }

    const q = (filter || '').trim()
    const filtered = filterTaTree(taTree, q)
    if (q) {
      const { properties, buildings } = expandAllTaKeys(filtered)
      taExpandedProperties.clear()
      properties.forEach((id) => taExpandedProperties.add(id))
      taExpandedBuildings.clear()
      buildings.forEach((key) => taExpandedBuildings.add(key))
    }

    const selected = new Set(selectedAssetIds.map(normalizeAssetId))
    const treeToRender = filtered
      .map((p) => ({
        ...p,
        buildings: p.buildings
          .map((b) => ({
            ...b,
            assets: b.assets.filter(
              (a) => !selected.has(normalizeAssetId(a.id))
            ),
          }))
          .filter((b) => b.assets.length > 0),
      }))
      .filter((p) => p.buildings.length > 0)

    if (treeToRender.length === 0) {
      appendTreeRow(assetsCombo.opts, 'Keine technische Anlage gefunden', 0, {
        cursor: 'default',
        muted: true,
      })
      return
    }

    for (const prop of treeToRender) {
      const propOpen = taExpandedProperties.has(prop.id)
      appendTreeRow(
        assetsCombo.opts,
        `${propOpen ? '▾' : '▸'} ${prop.name}`,
        0,
        {
          bold: true,
          onClick: () => {
            if (propOpen) taExpandedProperties.delete(prop.id)
            else taExpandedProperties.add(prop.id)
            renderAssetTree(filter)
          },
        }
      )
      if (!propOpen) continue

      for (const b of prop.buildings) {
        const bKey = `${prop.id}:${b.id}`
        const bOpen = taExpandedBuildings.has(bKey)
        appendTreeRow(assetsCombo.opts, `${bOpen ? '▾' : '▸'} ${b.name}`, 1, {
          onClick: () => {
            if (bOpen) taExpandedBuildings.delete(bKey)
            else taExpandedBuildings.add(bKey)
            renderAssetTree(filter)
          },
        })
        if (!bOpen) continue

        for (const asset of b.assets) {
          const isSelected = selected.has(normalizeAssetId(asset.id))
          appendTreeRow(
            assetsCombo.opts,
            `${isSelected ? '✓ ' : ''}${asset.name}${asset.typ ? ` (${asset.typ})` : ''}`,
            2,
            {
              bold: true,
              onClick: async () => {
                const assetId = normalizeAssetId(asset.id)
                const prev = selectedAssetIds.map(normalizeAssetId)
                if (isSelected) {
                  selectedAssetIds = prev.filter((x) => x !== assetId)
                } else {
                  selectedAssetIds = [...prev, assetId]
                }
                renderAssetChips()
                renderAssetTree(filter)
                const ok = await persistSubjectLinksFromCombo({ force: true })
                if (!ok) {
                  selectedAssetIds = prev
                  renderAssetChips()
                  renderAssetTree(filter)
                }
              },
            }
          )
        }
      }
    }
  }

  async function toggleWoTa(taId, filter) {
    const key = normalizeAssetId(taId)
    if (woExpandedTaIds.has(key)) {
      woExpandedTaIds.delete(key)
      renderWorkOrderTree(filter)
      return
    }
    woExpandedTaIds.add(key)
    renderWorkOrderTree(filter)
    await loadWorkOrdersForTa(key)
    renderWorkOrderTree(filter)
  }

  function workOrdersMatchQuery(list, q) {
    if (!q) return list
    const lower = q.toLowerCase()
    return list.filter((wo) => (wo.label || '').toLowerCase().includes(lower))
  }

  async function pickWorkOrder(wo) {
    const prevId = selectedWorkOrderId
    const prevLabel = workOrderDisplayLabel
    selectedWorkOrderId = wo.id
    workOrderDisplayLabel = wo.label
    woCombo.display.value = wo.label
    woCombo.drop.style.display = 'none'
    woCombo.deleteButton.style.display = 'block'
    const ok = await persistSubjectLinksFromCombo({ force: true })
    if (!ok) {
      selectedWorkOrderId = prevId
      workOrderDisplayLabel = prevLabel
      woCombo.display.value = prevLabel
      if (prevId == null) woCombo.deleteButton.style.display = 'none'
    }
  }

  function renderUnlinkedWorkOrdersSection(q) {
    if (workOrdersWithoutTa === null) return 0
    const all = workOrdersWithoutTa
    const wos = workOrdersMatchQuery(all, q)
    if (all.length === 0) return 0

    const sectionTitle = 'Ohne technische Anlage'
    const titleMatches =
      q && sectionTitle.toLowerCase().includes(q.toLowerCase().trim())
    if (q && wos.length === 0 && !titleMatches) return 0

    const open = woUnlinkedSectionOpen || Boolean(q)
    appendTreeRow(woCombo.opts, `${open ? '▾' : '▸'} ${sectionTitle}`, 0, {
      bold: true,
      onClick: () => {
        woUnlinkedSectionOpen = !woUnlinkedSectionOpen
        renderWorkOrderTree(woCombo.display.value)
      },
    })
    if (!open) return wos.length > 0 ? wos.length : 1

    if (woUnlinkedLoadPromise) {
      appendLoadingRow(woCombo.opts, 1, 'Aufträge werden geladen…')
      return wos.length
    }
    if (wos.length === 0) {
      appendTreeRow(woCombo.opts, 'Kein Auftrag in dieser Kategorie', 1, {
        cursor: 'default',
        muted: true,
      })
      return 0
    }
    for (const wo of wos) {
      appendTreeRow(woCombo.opts, wo.label, 1, {
        bold: true,
        onClick: () => pickWorkOrder(wo),
      })
    }
    return wos.length
  }

  function renderWorkOrderTree(filter) {
    woCombo.opts.innerHTML = ''
    if (!woTaTree) {
      return
    }

    const q = (filter || '').trim()
    const unlinkedCount = renderUnlinkedWorkOrdersSection(q)

    const filtered = filterTaTree(woTaTree, q)
    if (q) {
      const { properties, buildings } = expandAllTaKeys(filtered)
      woExpandedProperties.clear()
      properties.forEach((id) => woExpandedProperties.add(id))
      woExpandedBuildings.clear()
      buildings.forEach((key) => woExpandedBuildings.add(key))
    }

    if (filtered.length === 0 && unlinkedCount === 0) {
      appendTreeRow(woCombo.opts, 'Kein Auftrag gefunden', 0, {
        cursor: 'default',
        muted: true,
      })
      return
    }

    if (filtered.length === 0) {
      return
    }

    for (const prop of filtered) {
      const propOpen = woExpandedProperties.has(prop.id)
      appendTreeRow(woCombo.opts, `${propOpen ? '▾' : '▸'} ${prop.name}`, 0, {
        bold: true,
        onClick: () => {
          if (propOpen) woExpandedProperties.delete(prop.id)
          else woExpandedProperties.add(prop.id)
          renderWorkOrderTree(filter)
        },
      })
      if (!propOpen) continue

      for (const b of prop.buildings) {
        const bKey = `${prop.id}:${b.id}`
        const bOpen = woExpandedBuildings.has(bKey)
        appendTreeRow(woCombo.opts, `${bOpen ? '▾' : '▸'} ${b.name}`, 1, {
          onClick: () => {
            if (bOpen) woExpandedBuildings.delete(bKey)
            else woExpandedBuildings.add(bKey)
            renderWorkOrderTree(filter)
          },
        })
        if (!bOpen) continue

        for (const asset of b.assets) {
          const assetKey = normalizeAssetId(asset.id)
          const taOpen = woExpandedTaIds.has(assetKey)
          appendTreeRow(
            woCombo.opts,
            `${taOpen ? '▾' : '▸'} ${asset.name}${asset.typ ? ` (${asset.typ})` : ''}`,
            2,
            {
              onClick: () => toggleWoTa(assetKey, filter),
            }
          )
          if (!taOpen) continue

          if (woLoadingTaIds.has(assetKey)) {
            appendLoadingRow(woCombo.opts, 3, 'Aufträge werden geladen…')
            continue
          }

          const allForTa = workOrdersByTaId.get(assetKey) || []
          const wos = workOrdersMatchQuery(allForTa, q)
          if (wos.length === 0) {
            appendTreeRow(woCombo.opts, 'Keine Aufträge für diese Anlage', 3, {
              cursor: 'default',
              muted: true,
            })
            continue
          }

          for (const wo of wos) {
            appendTreeRow(woCombo.opts, wo.label, 3, {
              bold: true,
              onClick: () => pickWorkOrder(wo),
            })
          }
        }
      }
    }
  }

  async function fetchLinks() {
    const token = authHeader()
    if (!token) return null
    const params = new URLSearchParams({
      documentHrId: String(currentDocumentId),
    })
    const response = await fetch(
      `${baseApiUrl}/api/document/impower-links?${params}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
    if (!response.ok) return null
    return response.json()
  }

  async function initData() {
    assetsCombo.spinner.style.display = 'block'
    woCombo.spinner.style.display = 'block'
    try {
      const [data] = await Promise.all([fetchLinks(), ensureTaTree()])
      if (!data) return

      selectedAssetIds = (data.technicalAssetAssociations || []).map((a) =>
        normalizeAssetId(a.technicalAssetId)
      )
      committedAssetIds = [...selectedAssetIds]

      if (data.workOrderAssociation) {
        selectedWorkOrderId = data.workOrderAssociation.workOrderId
        committedWorkOrderId = selectedWorkOrderId
        workOrderDisplayLabel =
          data.linkedWorkOrder?.label || `#${selectedWorkOrderId}`
        woCombo.display.value = workOrderDisplayLabel
        woCombo.deleteButton.style.display = 'block'
      } else {
        selectedWorkOrderId = null
        committedWorkOrderId = null
        workOrderDisplayLabel = ''
        woCombo.display.value = ''
      }

      renderAssetChips()
      renderAssetTree('')
      renderWorkOrderTree('')
    } catch (e) {
      console.error('[documentEditorSubjectLinksComboBox] init:', e)
    } finally {
      assetsCombo.spinner.style.display = 'none'
      woCombo.spinner.style.display = 'none'
      dataLoaded = true
      setupEditModeSync()
    }
  }

  assetsCombo.display.addEventListener('click', async (e) => {
    if (!editMode) return
    e.stopPropagation()
    const opening = assetsCombo.drop.style.display === 'none'
    assetsCombo.drop.style.display = opening ? 'block' : 'none'
    if (opening) {
      assetsCombo.display.value = ''
      renderAssetTree('')
      assetsCombo.spinner.style.display = 'block'
      await ensureTaTree()
      assetsCombo.spinner.style.display = 'none'
      renderAssetChips()
      renderAssetTree('')
    }
  })

  assetsCombo.display.addEventListener('input', (e) => {
    if (!editMode) return
    renderAssetTree(e.target.value)
  })

  woCombo.display.addEventListener('click', async (e) => {
    if (!editMode) return
    e.stopPropagation()
    const opening = woCombo.drop.style.display === 'none'
    woCombo.drop.style.display = opening ? 'block' : 'none'
    if (opening) {
      woCombo.display.value = ''
      woCombo.deleteButton.style.display = 'none'
      renderWorkOrderTree('')
      woCombo.spinner.style.display = 'block'
      await Promise.all([ensureWoTaTree(), ensureWorkOrdersWithoutTa()])
      woCombo.spinner.style.display = 'none'
      renderWorkOrderTree('')
    }
  })

  woCombo.display.addEventListener('input', async (e) => {
    if (!editMode) return
    const v = e.target.value
    renderWorkOrderTree(v)
    const q = v.trim()
    if (!q || !woTaTree) return
    const filtered = filterTaTree(woTaTree, q)
    const taIds = []
    for (const p of filtered) {
      for (const b of p.buildings) {
        for (const a of b.assets) taIds.push(a.id)
      }
    }
    const toLoad = taIds
      .map(normalizeAssetId)
      .filter((id) => !workOrdersByTaId.has(id) && !woLoadingTaIds.has(id))
      .slice(0, 12)
    if (toLoad.length === 0) return
    await Promise.all(toLoad.map((id) => loadWorkOrdersForTa(id)))
    renderWorkOrderTree(v)
  })

  woCombo.deleteButton.addEventListener('click', async (e) => {
    e.stopPropagation()
    if (!editMode || !dataLoaded) return
    const prevId = selectedWorkOrderId
    const prevLabel = workOrderDisplayLabel
    selectedWorkOrderId = null
    workOrderDisplayLabel = ''
    woCombo.display.value = ''
    woCombo.deleteButton.style.display = 'none'
    const ok = await persistSubjectLinksFromCombo()
    if (!ok) {
      selectedWorkOrderId = prevId
      workOrderDisplayLabel = prevLabel
      woCombo.display.value = prevLabel
      if (prevId != null) woCombo.deleteButton.style.display = 'block'
    }
  })

  document.addEventListener('click', (e) => {
    if (!editMode) return
    if (chipsRow.contains(e.target)) return
    if (!assetsCombo.root.contains(e.target)) {
      assetsCombo.drop.style.display = 'none'
      assetsCombo.display.value = ''
    }
    if (!woCombo.root.contains(e.target)) {
      woCombo.drop.style.display = 'none'
      if (selectedWorkOrderId != null) {
        woCombo.display.value = workOrderDisplayLabel
        woCombo.deleteButton.style.display = 'block'
      } else {
        woCombo.display.value = ''
      }
    }
  })

  async function readDocumentTitle() {
    const rootEl = document.querySelector('#GeneralDocumentData')
    if (!rootEl) return ''
    for (const input of rootEl.querySelectorAll('.TextInput input.ant-input')) {
      if (
        assetsShell.wrapper.contains(input) ||
        woShell.wrapper.contains(input)
      )
        continue
      if (typeof input.value === 'string' && input.value.trim())
        return input.value.trim()
    }
    return ''
  }

  async function flushToServer({ force = false } = {}) {
    if (!force && !isDirty()) return true
    const token = authHeader()
    if (!token) return false

    const assetIdsToSave = selectedAssetIds.map(normalizeAssetId)

    persistInFlight += 1
    assetsCombo.root.style.opacity = '0.7'
    woCombo.root.style.opacity = '0.7'
    try {
      const title = await readDocumentTitle()
      const response = await fetch(`${baseApiUrl}/api/document/impower-links`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentHrId: currentDocumentId,
          technicalAssetIds: assetIdsToSave,
          workOrderId: selectedWorkOrderId,
          ...(title ? { name: title } : {}),
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        console.error(
          '[documentEditorSubjectLinksComboBox] PUT failed:',
          data.error
        )
        return false
      }
      selectedAssetIds = assetIdsToSave
      committedAssetIds = [...assetIdsToSave]
      committedWorkOrderId = selectedWorkOrderId
      return true
    } catch (e) {
      console.error('[documentEditorSubjectLinksComboBox] flush:', e)
      return false
    } finally {
      persistInFlight = Math.max(0, persistInFlight - 1)
      const ro = !editMode
      assetsCombo.root.style.opacity = ro ? '0.72' : '1'
      woCombo.root.style.opacity = ro ? '0.72' : '1'
    }
  }

  async function persistSubjectLinksFromCombo({ force = false } = {}) {
    if (!dataLoaded) return true
    let done
    const ticket = new Promise((resolve) => {
      done = resolve
    })
    persistChain = persistChain.then(async () => {
      try {
        const ok = await flushToServer({ force })
        if (!ok) {
          woCombo.display.style.boxShadow = '0 0 0 2px red'
          woCombo.display.value = 'Fehler beim Speichern der Zuordnung'
          done(false)
          return false
        }
        if (selectedWorkOrderId != null) {
          woCombo.display.value = workOrderDisplayLabel
          woCombo.display.style.boxShadow = '0 0 0 1px #d9d9d9'
        }
        renderAssetChips()
        done(true)
        return true
      } catch (e) {
        console.error('[documentEditorSubjectLinksComboBox] persist:', e)
        done(false)
        return false
      }
    })
    return ticket
  }

  const SAVE_LABELS = [
    'speichern',
    'save',
    'save all',
    'save and continue',
    'speichern und weiter',
    'als entwurf speichern',
  ]
  const EDIT_LABELS = ['bearbeiten', 'edit']

  function detectImpowerEditMode() {
    const pageHeader =
      document.querySelector('.DocumentEditor.page .PageHeader') ||
      document.querySelector('.overlay-page .DocumentEditor .PageHeader')
    const area = pageHeader?.querySelector('.right-side') || pageHeader
    const texts = area
      ? Array.from(area.querySelectorAll('button'))
          .filter((btn) => btn.offsetParent !== null && !btn.disabled)
          .map((btn) =>
            (btn.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase()
          )
      : []
    if (SAVE_LABELS.some((l) => texts.includes(l))) return true
    if (EDIT_LABELS.some((l) => texts.includes(l))) return false
    const general = document.querySelector('#GeneralDocumentData')
    if (!general) return false
    return !!general.querySelector(
      'input:not([disabled]):not([type="hidden"]), textarea:not([disabled])'
    )
  }

  let editSyncObserver = null
  let editSyncDebounce = null

  function syncEditMode() {
    if (!dataLoaded) return
    if (removingAssetIds.size > 0 || persistInFlight > 0) return
    const inEdit = detectImpowerEditMode()
    if (lastSyncedEditMode === inEdit) return
    lastSyncedEditMode = inEdit
    setReadOnly(!inEdit)
    renderAssetChips()
  }

  function setupEditModeSync() {
    lastSyncedEditMode = null
    const inEdit = detectImpowerEditMode()
    lastSyncedEditMode = inEdit
    setReadOnly(!inEdit)
    renderAssetChips()
    ;[100, 300, 600, 1200].forEach((ms) => setTimeout(syncEditMode, ms))
    if (editSyncObserver) editSyncObserver.disconnect()
    editSyncObserver = new MutationObserver(() => {
      clearTimeout(editSyncDebounce)
      editSyncDebounce = setTimeout(syncEditMode, 120)
    })
    editSyncObserver.observe(document.body, { childList: true, subtree: true })
  }

  setReadOnly(true)
  initData()

  generalBlock.addEventListener('click', () => {
    setTimeout(() => {
      section = document.querySelector(
        '#GeneralDocumentData .section-content > .ant-row'
      )
      if (!section) return
      if (!section.contains(assetsShell.wrapper))
        section.appendChild(assetsShell.wrapper)
      if (!section.contains(woShell.wrapper))
        section.appendChild(woShell.wrapper)
    }, 0)
  })
}
